import type { GraphQLAuditDecision } from '@internal-audit/domain/graphql-types';
import { Text } from '@soteria/design-system-ve';
import { useMemo, useCallback } from 'react';

import * as styles from '../AuditItem.css';
import { calculateDecisionStatistics } from '../statistics/decisionStatistics';

interface DecisionStatsProps {
  decisions: GraphQLAuditDecision[];
}

export function DecisionStats({ decisions }: DecisionStatsProps) {
  // Calculate statistics with useMemo for performance
  const stats = useMemo(() => {
    // Convert GraphQL decisions to expected format for statistics calculation
    const convertedDecisions = decisions.map(decision => ({
      ...decision,
      type: 'auditDecision' as const, // Add required type property
    }));

    return calculateDecisionStatistics(convertedDecisions);
  }, [decisions]);

  // Helper function to get decision statistics text
  const getDecisionStatsText = useCallback(
    (decisions: GraphQLAuditDecision[], stats: { hasConflicts: boolean; conflictCount: number; agreementLevel: number }): string => {
      if (decisions.length === 0) {
        return ''; // No text when no decisions are visible
      }

      if (decisions.length === 1) {
        return 'Single Decision'; // Provide text for single decisions
      }

      if (stats.hasConflicts) {
        return `${stats.conflictCount} Conflict${stats.conflictCount !== 1 ? 's' : ''}`;
      }

      if (stats.agreementLevel === 100) {
        return 'Full Agreement';
      }

      return `${stats.agreementLevel}% Agreement`;
    },
    []
  );

  // Helper function to get decision stats CSS class
  const getDecisionStatsClass = useCallback((decisions: GraphQLAuditDecision[], stats: { hasConflicts: boolean; agreementLevel: number }): string => {
    if (decisions.length === 0) return '';

    if (decisions.length === 1) return styles.fullAgreementText; // Use same styling as full agreement for single decisions

    if (stats.hasConflicts) return styles.conflictText;
    if (stats.agreementLevel === 100) return styles.fullAgreementText;

    return styles.partialAgreementText;
  }, []);

  const statsText = getDecisionStatsText(decisions, stats);
  const statsClass = getDecisionStatsClass(decisions, stats);

  // Don't render anything if there's no text to display
  if (!statsText) {
    return null;
  }

  return <Text className={`${styles.decisionsDescription} ${statsClass}`}>{statsText}</Text>;
}
